import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  MONTH_SHORT_LABELS,
  WEEKDAY_LABELS,
  buildMonthGrid,
  filterCalendarEvents,
  formatMonthTitle,
  formatShowDateLabel,
  getManilaYearMonth,
  groupEventsByShowDate,
  monthHasEvents,
  shiftMonth,
} from "./eventCalendar";
import { getManilaCalendarYmd } from "../lib/dateTime";

const STATUS_DOT = {
  Draft: "bg-gray-400",
  Selling: "bg-orange-500",
  "Sold Out": "bg-red-500",
  Past: "bg-slate-400",
};

const CALENDAR_LEGEND = [
  { label: "Selling", swatch: "rounded-sm bg-orange-100 border border-orange-200" },
  { label: "Sold Out", swatch: "rounded-sm bg-red-100 border border-red-200" },
  { label: "Draft", swatch: "rounded-sm bg-gray-100 border border-gray-300" },
  { label: "Past", swatch: "rounded-sm bg-slate-200 border border-slate-300" },
  { label: "Today", swatch: "rounded-full bg-blue-600" },
  { label: "Selected", swatch: "rounded-sm border border-amber-300 bg-amber-50" },
];

const formatEventTime = (time) => {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(time || "").trim());
  if (!match) return "";
  const hours = Number(match[1]);
  const suffix = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${hour12}:${match[2]} ${suffix}`;
};

const EventChip = ({ event, selected, onOpen }) => (
  <button
    type="button"
    onClick={(clickEvent) => {
      clickEvent.stopPropagation();
      onOpen(event.id);
    }}
    title={event.title || "Untitled event"}
    className={`block w-full truncate rounded px-1 py-0.5 text-left text-[10px] font-semibold leading-tight ${
      selected ? "border border-amber-300 bg-amber-50 text-amber-950" : event.statusClass || "bg-gray-100 text-gray-700"
    }`}
  >
    {event.title || "Untitled event"}
  </button>
);

export default function AdminEventCalendar({
  events = [],
  search = "",
  selectedEventId = "",
  loading = false,
  onOpenEvent,
}) {
  const todayYmd = getManilaCalendarYmd();
  const initialMonth = getManilaYearMonth();
  const [visibleMonth, setVisibleMonth] = useState(initialMonth);
  const [openDay, setOpenDay] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(initialMonth.year);

  const visibleEvents = useMemo(
    () => filterCalendarEvents(events, search),
    [events, search]
  );
  const eventsByDate = useMemo(() => groupEventsByShowDate(visibleEvents), [visibleEvents]);
  const cells = useMemo(
    () => buildMonthGrid(visibleMonth.year, visibleMonth.month),
    [visibleMonth]
  );
  const openEvents = eventsByDate[openDay] || [];
  const hasEventsThisMonth = monthHasEvents(eventsByDate, visibleMonth.year, visibleMonth.month);

  useEffect(() => {
    if (openDay && !eventsByDate[openDay]) {
      setOpenDay("");
    }
  }, [eventsByDate, openDay]);

  useEffect(() => {
    if (!pickerOpen) return undefined;

    const closeOnOutside = (event) => {
      if (!event.target.closest("[data-month-picker]")) {
        setPickerOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setPickerOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [pickerOpen]);

  const goToToday = () => {
    const current = getManilaYearMonth();
    setVisibleMonth(current);
    setPickerYear(current.year);
    setOpenDay("");
    setPickerOpen(false);
  };

  const changeMonth = (delta) => {
    setVisibleMonth((current) => shiftMonth(current.year, current.month, delta));
    setOpenDay("");
  };

  const togglePicker = () => {
    setPickerYear(visibleMonth.year);
    setPickerOpen((open) => !open);
  };

  const chooseMonth = (month) => {
    setVisibleMonth({ year: pickerYear, month });
    setOpenDay("");
    setPickerOpen(false);
  };

  return (
    <div className="bg-white border border-gray-300 rounded-2xl p-4 sm:p-5 min-w-0">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
        <h3 className="font-bold text-2xl text-gray-900">Event Calendar</h3>
        <div className="flex flex-wrap items-center gap-1 text-xs font-semibold">
          <button
            type="button"
            onClick={() => changeMonth(-1)}
            className="grid h-8 w-8 place-items-center rounded-md text-gray-600 hover:bg-gray-100"
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="relative" data-month-picker="true">
            <button
              type="button"
              onClick={togglePicker}
              aria-expanded={pickerOpen}
              aria-haspopup="dialog"
              className="min-w-[9.5rem] rounded-md px-2 py-1 text-center text-sm font-bold text-gray-900 hover:bg-gray-100"
            >
              {formatMonthTitle(visibleMonth.year, visibleMonth.month)}
            </button>
            {pickerOpen && (
              <div
                role="dialog"
                aria-label="Choose month"
                className="absolute right-0 top-full z-30 mt-1 w-56 rounded-lg border border-gray-200 bg-white p-3 shadow-xl"
              >
                <div className="mb-2 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setPickerYear((year) => year - 1)}
                    className="grid h-7 w-7 place-items-center rounded-md text-gray-600 hover:bg-gray-100"
                    aria-label="Previous year"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <p className="text-sm font-bold text-gray-900">{pickerYear}</p>
                  <button
                    type="button"
                    onClick={() => setPickerYear((year) => year + 1)}
                    className="grid h-7 w-7 place-items-center rounded-md text-gray-600 hover:bg-gray-100"
                    aria-label="Next year"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-1">
                  {MONTH_SHORT_LABELS.map((label, index) => {
                    const month = index + 1;
                    const selected = pickerYear === visibleMonth.year && month === visibleMonth.month;
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => chooseMonth(month)}
                        className={`rounded-md px-1 py-2 text-xs font-semibold ${
                          selected ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 flex justify-end gap-3 text-xs font-semibold">
                  <button type="button" onClick={goToToday} className="text-blue-600 hover:underline">
                    Today
                  </button>
                  <button type="button" onClick={() => setPickerOpen(false)} className="text-gray-500 hover:underline">
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => changeMonth(1)}
            className="grid h-8 w-8 place-items-center rounded-md text-gray-600 hover:bg-gray-100"
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </button>
          <button
            type="button"
            onClick={goToToday}
            className="ml-1 rounded-md px-2 py-1 text-blue-600 hover:bg-blue-50"
          >
            Today
          </button>
        </div>
      </div>

      <ul className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold text-gray-600" aria-label="Calendar legend">
        {CALENDAR_LEGEND.map((item) => (
          <li key={item.label} className="inline-flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 shrink-0 ${item.swatch}`} />
            {item.label}
          </li>
        ))}
      </ul>

      {loading ? (
        <div className="h-64 rounded-lg bg-gray-50 flex items-center justify-center text-gray-500 text-sm">
          Loading events...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200">
            {WEEKDAY_LABELS.map((label) => (
              <div key={label} className="bg-gray-50 px-1 py-2 text-center text-[10px] font-bold uppercase text-gray-500">
                {label}
              </div>
            ))}
            {cells.map((cell) => {
              const dayEvents = eventsByDate[cell.ymd] || [];
              const shown = dayEvents.slice(0, 2);
              const extra = dayEvents.length - shown.length;
              const isToday = cell.ymd === todayYmd;
              const isOpen = cell.ymd === openDay;

              return (
                <div
                  key={cell.ymd}
                  className={`min-h-[4.25rem] p-1 sm:min-h-[5.5rem] ${
                    cell.inMonth ? "bg-white" : "bg-gray-50"
                  } ${isOpen ? "ring-1 ring-inset ring-blue-400" : ""}`}
                >
                  <div className="mb-1 flex items-center justify-between gap-1">
                    {dayEvents.length ? (
                      <button
                        type="button"
                        onClick={() => setOpenDay(cell.ymd)}
                        className={`inline-grid h-5 min-w-5 place-items-center rounded-full px-1 text-[11px] font-bold ${
                          isToday
                            ? "bg-blue-600 text-white ring-2 ring-blue-200"
                            : cell.inMonth
                              ? "text-gray-800 hover:bg-gray-100"
                              : "text-gray-400 hover:bg-gray-100"
                        }`}
                        aria-label={`Show events on ${formatShowDateLabel(cell.ymd)}`}
                      >
                        {cell.day}
                      </button>
                    ) : (
                      <span
                        className={`inline-grid h-5 min-w-5 place-items-center rounded-full px-1 text-[11px] font-bold ${
                          isToday
                            ? "bg-blue-600 text-white ring-2 ring-blue-200"
                            : cell.inMonth
                              ? "text-gray-800"
                              : "text-gray-400"
                        }`}
                      >
                        {cell.day}
                      </span>
                    )}
                    {dayEvents.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setOpenDay(cell.ymd)}
                        className="text-[10px] font-semibold text-gray-500 sm:hidden"
                        aria-label={`${dayEvents.length} events`}
                      >
                        {dayEvents.length}
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1 sm:hidden">
                    {dayEvents.slice(0, 3).map((event) => (
                      <span
                        key={event.id}
                        className={`h-1.5 w-1.5 rounded-full ${
                          String(event.id) === String(selectedEventId)
                            ? "bg-amber-500"
                            : STATUS_DOT[event.statusLabel] || "bg-gray-400"
                        }`}
                      />
                    ))}
                  </div>

                  <div className="hidden space-y-1 sm:block">
                    {shown.map((event) => (
                      <EventChip
                        key={event.id}
                        event={event}
                        selected={String(event.id) === String(selectedEventId)}
                        onOpen={onOpenEvent}
                      />
                    ))}
                    {extra > 0 && (
                      <button
                        type="button"
                        onClick={(clickEvent) => {
                          clickEvent.stopPropagation();
                          setOpenDay(cell.ymd);
                        }}
                        className="text-[10px] font-bold text-blue-600 hover:underline"
                      >
                        +{extra}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {!hasEventsThisMonth && (
            <p className="mt-3 text-center text-sm text-gray-500">No events this month.</p>
          )}

          {openEvents.length > 0 && (
            <div className="mt-3 overflow-hidden rounded-lg border border-gray-200">
              <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
                <p className="text-sm font-bold text-gray-900">{formatShowDateLabel(openDay)}</p>
                <button
                  type="button"
                  onClick={() => setOpenDay("")}
                  className="text-xs font-semibold text-gray-500 hover:text-gray-800"
                >
                  Close
                </button>
              </div>
              <ul>
                {openEvents.map((event) => {
                  const timeLabel = formatEventTime(event.time);
                  const selected = String(event.id) === String(selectedEventId);
                  return (
                    <li key={event.id} className="border-b border-gray-100 last:border-b-0">
                      <button
                        type="button"
                        onClick={() => onOpenEvent(event.id)}
                        className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-gray-50 ${
                          selected ? "bg-amber-50" : ""
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-gray-900">
                            {event.title || "Untitled event"}
                          </span>
                          <span className="block truncate text-[11px] text-gray-500">
                            {[timeLabel, event.venue].filter(Boolean).join(" · ") || "No time set"}
                          </span>
                        </span>
                        <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-bold ${event.statusClass || "bg-gray-100 text-gray-700"}`}>
                          {event.statusLabel || "Draft"}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
